import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, authApi } from '@/lib/api-client';
import { useAuthStore, type AuthUser, type AuthTenant } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';
import { useThemeStore } from '@/stores/theme-store';
import type { BrandingConfig } from '@supernova/shared';

async function fetchAndApplyBranding() {
  try {
    const res = await api.get<{ data: BrandingConfig }>('/branding');
    if (res.data) {
      useThemeStore.getState().setBranding(res.data);
    }
  } catch {
    // Branding fetch failed — keep defaults
  }
}

interface AuthUserResponse {
  data: {
    user: AuthUser;
  };
}

interface AuthLoginResponse {
  data: {
    user?: AuthUser;
    requires2FA?: boolean;
    pendingToken?: string;
  };
}

interface AuthMeResponse {
  data: AuthUser;
}

interface TenantResponse {
  data: (AuthTenant & { locationId?: string }) | null;
}

export function useAuthCheck() {
  const { setAuth, clearAuth, setLoading, isLoading } = useAuthStore();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    setLoading(true);

    // Fetch user from auth service, then tenant from dashboard API
    authApi
      .get<AuthMeResponse>('/auth/me')
      .then(async (res) => {
        const user = res.data;

        // Super admin has no tenant
        if (user.role === 'super_admin' || !user.tenantId) {
          setAuth(user, null);
          return;
        }

        // Fetch tenant data from dashboard API
        try {
          const tenantRes = await api.get<TenantResponse>('/tenants/me');
          setAuth(user, tenantRes.data);
        } catch {
          // Tenant fetch failed — set user without tenant
          setAuth(user, null);
        }

        // Fetch and apply tenant branding (non-blocking)
        fetchAndApplyBranding();
      })
      .catch(() => {
        clearAuth();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoading;
}

export function useLogin() {
  const { setAuth } = useAuthStore();
  const { clearSelectedLocation } = useLocationStore();
  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async (data: { email: string; password: string }) => {
      const authRes = await authApi.post<AuthLoginResponse>('/auth/login', data);

      // 2FA required — return early without setting auth
      if (authRes.data.requires2FA) {
        return { requires2FA: true as const, pendingToken: authRes.data.pendingToken! };
      }

      const user = authRes.data.user!;

      // Fetch tenant from dashboard API
      let tenant: AuthTenant | null = null;
      if (user.tenantId) {
        try {
          const tenantRes = await api.get<TenantResponse>('/tenants/me');
          tenant = tenantRes.data;
        } catch {
          // Tenant fetch may fail if tenant doesn't exist yet
        }
      }

      return { requires2FA: false as const, user, tenant };
    },
    onSuccess: (res) => {
      if (!res.requires2FA) {
        clearSelectedLocation();
        setAuth(res.user, res.tenant);
        fetchAndApplyBranding();
      }
    },
  });
}

export function useAdminLogin() {
  const { setAuth } = useAuthStore();
  const { clearSelectedLocation } = useLocationStore();
  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async (data: { email: string; password: string }) => {
      const authRes = await authApi.post<AuthLoginResponse>('/auth/admin/login', data);

      if (authRes.data.requires2FA) {
        return { requires2FA: true as const, pendingToken: authRes.data.pendingToken! };
      }

      return { requires2FA: false as const, user: authRes.data.user!, tenant: null };
    },
    onSuccess: (res) => {
      if (!res.requires2FA) {
        clearSelectedLocation();
        setAuth(res.user, res.tenant);
      }
    },
  });
}

export function useSignup() {
  const { setAuth } = useAuthStore();
  const { setSelectedLocation } = useLocationStore();
  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async (data: {
      businessName: string;
      name: string;
      email: string;
      password: string;
      phone: string;
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
      type?: 'company' | 'warehouse' | 'esl';
    }) => {
      // Step 1: Create user in auth service
      const { name, email, password } = data;
      const authRes = await authApi.post<AuthUserResponse>('/auth/signup', { name, email, password });
      const user = authRes.data.user;

      // Step 2: Create tenant + auto-create first location in dashboard API
      const { businessName, phone, street, city, state, zip, country, type } = data;
      const tenantRes = await api.post<TenantResponse>('/tenants', {
        businessName,
        phone,
        street,
        city,
        state,
        zip,
        country,
        type: type || 'company',
      });
      const tenant = tenantRes.data;
      const locationId = tenant?.locationId;

      // Step 3: Refresh token to get updated JWT with tenantId
      await authApi.post('/auth/refresh');

      return { user: { ...user, tenantId: tenant?.id || null }, tenant, locationId };
    },
    onSuccess: (res) => {
      setAuth(res.user, res.tenant);
      // Auto-select the first location created during signup
      if (res.locationId && res.tenant?.name) {
        setSelectedLocation(res.locationId, res.tenant.name);
      }
    },
  });
}

export function useLogout() {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.post('/auth/logout'),
    onSettled: () => {
      const isAdmin = window.location.hash.startsWith('#/admin');
      clearAuth();
      useLocationStore.getState().clearSelectedLocation();
      queryClient.clear();
      window.location.hash = isAdmin ? '#/admin/login' : '#/login';
    },
  });
}
